<template>
  <div class="pt-mask" @click.self="close">
    <div class="pt-modal" :class="{ 'pt-modal-full': membership }">
      <div class="pt-head">
        <h4>{{ membership ? '멤버쉽 캐시백 지도(관리자)' : '후원 조직도' }}</h4>
        <div class="pt-head-right">
          <span class="pt-legend"><i class="dot inactive"></i>Non-active / 통산매출0(흰색)</span>
          <button class="pt-x" @click="close">닫기</button>
        </div>
      </div>

      <!-- 스펙27: 네모 칸 색상표(직급별). 검색 조건 바로 위에 표시 -->
      <div class="pt-colorbar">
        <span class="pt-colorbar-title">직급 색상표</span>
        <span v-for="r in rankColorList" :key="r.name" class="pt-color-item">
          <i class="sw" :style="r.grad
              ? { background: 'linear-gradient(90deg,#0A4DA4 50%,#DE2F2C 50%)' }
              : { background: '#' + r.hex }"></i>{{ displayRank(r.name) }}
        </span>
      </div>

      <!-- 20007 검색조건 배치도: 센터(에디트 비활성/검색/비우기) · 이름(에디트 비활성/검색/비우기) · 단수 · 라디오 · 확대축소 · 시점 · 월 · 조회 -->
      <div class="pt-toolbar">
        <label>센터</label>
        <input class="cond" type="text" :value="centerName" placeholder="" disabled />
        <button class="primary" @click="centerOpen = true">검색</button>
        <button class="ghost" @click="clearCenter">비우기</button>
        <label>이름</label>
        <!-- 조직도 팝업창 내 이름 조회 입력받기 프로젝트 -->
        <input class="cond cond-edit" type="text" v-model="custName" placeholder="" @input="custLoginId = ''" @keyup.enter="doSearch" />
        <button class="primary" @click="searchOpen = true">검색</button>
        <button class="ghost" @click="clearMember">비우기</button>
        <!-- 스펙24003: 단수 text 및 단수 에디트는 화면에서만 감춘다(바인딩/로직은 유지). -->
        <label style="display:none">단수</label>
        <input v-model.number="level" type="number" min="1" class="lvl" disabled @keyup.enter="doSearch" style="display:none" />

        <span class="pt-radios">
          <label class="rd"><input type="radio" value="L" v-model="side" /> 나의 좌측</label>
          <label class="rd"><input type="radio" value="R" v-model="side" /> 나의 우측</label>
          <label class="rd"><input type="radio" value="B" v-model="side" /> 나의 좌/우측</label>
        </span>

        <span class="pt-sep"></span>
        <span class="pt-zoom-label">확대/축소</span>
        <button class="pt-zoom" @click="zoomOut" :disabled="zoom <= 0.1" title="축소">−</button>
        <span class="pt-zoom-val">{{ Math.round(zoom * 100) }}%</span>
        <button class="pt-zoom" @click="zoomIn" :disabled="zoom >= 1" title="확대">+</button>

        <!-- 3008: 조회 버튼 좌측 "시점" 콤보(이력 스냅샷). admin 전용(staff/customer 미표시=항상 현재/라이브).
             배치일마다 수당계산직전(BEFORE)/직후(AFTER) 두 항목, 마지막은 "현재"(라이브 GRE_NODE). 진입 기본=최신 수당계산직후. -->
        <template v-if="isAdminUser">
          <span class="pt-sep"></span>
          <span class="pt-zoom-label">시점</span>
          <select class="pt-snapsel" v-model="selectedPoint" title="조회 시점 선택">
            <option v-for="opt in snapOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
          <!-- 3008: 조회 버튼과 콤보 사이 "월" 선택기. 콤보는 이 달의 수당계산직전/직후만 표시(+현재). -->
          <span class="pt-zoom-label">월</span>
          <input type="month" class="pt-basedate" v-model="selectedMonth" :max="curMonth" title="조회 월 선택" />
        </template>

        <button class="primary" @click="doSearch">조회</button>

        <span class="pt-summary-inline" v-if="!loading && !error && layoutNodes.length">
          기준 <b>{{ rootLabel }}</b> · {{ side === 'L' ? '좌측' : (side === 'R' ? '우측' : '좌/우측') }}
        </span>
      </div>

      <div class="pt-body pt-grab" @scroll="onCanvasScroll"
           @mousedown="onPanStart" @mousemove="onPanMove" @mouseup="onPanEnd" @mouseleave="onPanEnd">
        <div v-if="loading" class="pt-msg">불러오는 중...</div>
        <div v-else-if="error" class="pt-msg err">{{ error }}</div>
        <div v-else-if="!layoutNodes.length" class="pt-msg">이름 항목의 검색 버튼으로 회원을 선택한 뒤 [조회]를 누르세요.</div>

        <div v-else class="pt-canvas">
          <svg :width="svgW * zoom" :height="svgH * zoom" :viewBox="'0 0 ' + svgW + ' ' + svgH">
            <defs>
              <!-- 블루/레드 다이아몬드 : 좌 50% 청색, 우 50% 적색 그라데이션 -->
              <linearGradient id="ptGradDia" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#0A4DA4" /><stop offset="50%" stop-color="#0A4DA4" />
                <stop offset="50%" stop-color="#DE2F2C" /><stop offset="100%" stop-color="#DE2F2C" />
              </linearGradient>
            </defs>
            <!-- 스펙24003: 조회 버튼 아래 흰색 여백 바로 아래 '청색 세모'(지붕 폴리곤) 제거 -->
            <!-- <polygon :points="roofPoints" class="pt-roof" /> -->
            <rect :x="frameX" :y="frameY" :width="frameW" :height="frameH" rx="6" class="pt-frame" />

            <g v-for="b in floorBands" :key="'band-' + b.floor">
              <rect :x="frameX" :y="b.y" :width="frameW" :height="floorH"
                    :class="['pt-band', b.floor % 2 === 0 ? 'even' : 'odd']" />
              <line :x1="frameX" :y1="b.y" :x2="frameX + frameW" :y2="b.y" class="pt-floor-line" />
              <!-- 개선2_61: 0단 기준 라벨(나=0단). 멤버쉽 지도는 맨좌측 2번째 행(floor>=2)부터 '1단'으로 표시(루트행 라벨 없음) -->
              <text v-if="b.floor >= 2" :x="frameX - 8" :y="b.y + floorH / 2 + 4" text-anchor="end" class="pt-floor-label">
                {{ b.floor - 1 }}단
              </text>
            </g>

            <path v-for="(e, i) in layoutEdges" :key="'edge-' + i" :d="e.d" class="pt-edge" />

            <g v-for="n in layoutNodes" :key="'node-' + n.id"
               :transform="'translate(' + n.x + ',' + n.y + ')'"
               :style="isAdminUser ? 'cursor:pointer' : ''"
               @click.stop="onNodeClick(n)">
              <!-- 개선2_76: 멤버쉽 지도에서 2번째 행(floor>=2)부터 '금액 있는' 네모칸은 적색 테두리(어제/통산 선택값에 따라 감지) -->
              <rect :width="nodeW" :height="nodeH" rx="6" class="pt-node"
                    :fill="nodeFill(n)"
                    :stroke="memHasAmt(n) ? '#e01010' : nodeStroke(n)"
                    :stroke-width="memHasAmt(n) ? 2.6 : 1.4" />
              <text :x="nodeW / 2" :y="15" text-anchor="middle"
                    :fill="nodeText(n)" class="pt-node-name">{{ n.name }}</text>
              <text :x="nodeW / 2" :y="28" text-anchor="middle"
                    :fill="nodeText(n)" class="pt-node-sub">{{ n.loginId }}</text>
              <text :x="nodeW / 2" :y="39" text-anchor="middle"
                    :fill="nodeText(n)" class="pt-node-rank">{{ displayRank(n.rankName) }}</text>
              <!-- 스펙24003: 직급 아래 가입일/첫구매일/최근재구매일. 상하여백 11px, 폰트 9.5px.
                   첫구매만 있고 재구매 없으면 최근재구매일 줄은 미표시(단, 네모칸 공간은 그대로 차지). -->
              <text :x="nodeW / 2" :y="50" text-anchor="middle"
                    :fill="nodeText(n)" class="pt-node-join">가입일: {{ n.joinDt }}</text>
              <text v-if="n.firstBuyDt" :x="nodeW / 2" :y="61" text-anchor="middle"
                    :fill="nodeText(n)" class="pt-node-join">첫 구매일: {{ n.firstBuyDt }}</text>
              <text v-if="n.lastRepurchaseDt" :x="nodeW / 2" :y="72" text-anchor="middle"
                    :fill="nodeText(n)" class="pt-node-join">최근 재구매일: {{ n.lastRepurchaseDt }}</text>
              <!-- 개선2_61/3008: 멤버쉽 지도 - 날짜줄 아래에 각자 '자신만의 멤버쉽 광고캐시백 금액'(기준일 기준) 표시 -->
              <text v-if="membership && shownAmounts" :x="nodeW / 2" :y="85" text-anchor="middle"
                    :fill="nodeText(n)" class="pt-node-mem">{{ memFmt(n.membershipCb) }}</text>
              <text v-if="n.leg" :x="5" :y="12" :fill="nodeText(n)" class="pt-leg-badge">{{ n.leg }}</text>
            </g>
          </svg>
        </div>
      </div>
    </div>

    <!-- 20007: 이름 항목 검색 = 회원 검색 팝업, 센터 항목 검색 = 센터 검색 팝업 -->
    <SettlementMemberSearchDialog v-if="searchOpen" :self-children-only="true" @select="onUserSelect" @close="searchOpen = false" />
    <SettlementCenterSearchDialog v-if="centerOpen" @select="onCenterSelect" @close="centerOpen = false" />
  </div>
</template>


<script>
import api from '../api';
import { isAdmin } from '../store/auth';
import SettlementMemberSearchDialog from './SettlementMemberSearchDialog.vue';
import SettlementCenterSearchDialog from './SettlementCenterSearchDialog.vue';

export default {
  name: 'PositionTreeDialog',
  components: { SettlementMemberSearchDialog, SettlementCenterSearchDialog },
  props: {
    // 개선2_61: true 면 [멤버쉽 캐시백 지도(관리자)] 모드(노드별 멤버쉽 광고캐시백 표시, 전 단수 지연로딩)
    membership: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      error: '',
      selectedUser: null,  // 20007: 이름 항목으로 선택된 사용자 { loginId, name }
      //조직도 팝업창 내 이름 조회 입력받기 프로젝트
      custName: '', custLoginId: '',
      searchOpen: false,   // 회원 검색 팝업 표시
      centerOpen: false,   // 20007: 센터 검색 팝업 표시
      centerId: null, centerName: '',  // 20007: 센터 항목으로 선택된 센터
      level: 5,            // 단수(1 이상, 스펙200: 상한 제거 / 스펙234_개선_18: 초기값 5)
      side: 'B',           // 좌측(L) / 우측(R) / 좌·우측(B) - 스펙104: 초기값 좌/우측
      zoom: 1,             // 스펙104: 초기/조회시 그래픽 비율 100%
      // 3008: 시점 콤보. selectedPoint='CURRENT'(현재/라이브) 또는 'BEFORE|YYYY-MM-DD'/'AFTER|YYYY-MM-DD'(이력).
      //   콤보는 admin 전용. staff/customer 는 콤보 미표시 + 항상 '현재'(라이브 GRE_NODE).
      isAdminUser: false,
      selectedPoint: 'CURRENT',
      snapPoints: [],       // 3008: 전체 이력 스냅샷 목록 [{dt,'YYYY-MM-DD', t:'BEFORE'/'AFTER'}]
      selectedMonth: '',    // 3008: 월 선택기(YYYY-MM). 콤보는 이 달의 항목만 표시.
      // 3008: 노드 금액 표시 여부. '현재' 또는 '수당계산직후(AFTER)'만 표시, '수당계산직전(BEFORE)'은 숨김.
      //   (조회 시점의 선택으로 확정 - 콤보만 바꾸고 미조회 상태에서 표시가 바뀌지 않도록)
      shownAmounts: true,
      maxDepthLimit: 25,
      rootLabel: '',
      shownFloors: [],
      // 레이아웃 상수
      nodeW: 140, nodeH: 80, hGap: 18, vGap: 34, gutter: 46, pad: 8,
      // 개선2_61: 멤버쉽 지도 - 아래로 10단씩 지연 로딩(초기 0~14단), 전체 트리 보관
      shownDepthCap: 15, treeRoot: null,
      // 개선2_62: 멤버쉽 지도 마우스 드래그(팬) 상태
      panning: false, panX: 0, panY: 0, panSL: 0, panST: 0,
      layoutNodes: [],
      layoutEdges: [],
      floorBands: [],
      svgW: 400,
      svgH: 300,
      // 스펙27 직급별 색상표(RGB). grad=true 는 청/적 그라데이션.
      rankColorList: [
        { name: '그린스타', hex: '00FF00' }, { name: '그린스타B', hex: '009C00' }, { name: '옐로스타', hex: 'FCCC44' },
        { name: '퍼플스타', hex: '7C4D9D' }, { name: '실버스타', hex: 'C1C0C5' },
        { name: '골드스타', hex: 'EBC04F' }, { name: '루비', hex: 'E31F21' },
        { name: '에메랄드', hex: '0A6544' }, { name: '다이아몬드', hex: '0A4DA4' },
        { name: '블루 다이아몬드', grad: true }, { name: '레드 다이아몬드', grad: true },
        { name: '크라운', hex: '031851' }, { name: '엠버서드', hex: '994B24' },
        { name: '임페리얼', hex: '55308F' }, { name: '퍼스트', hex: '076B7A' },
        { name: 'VIP', hex: '011F55' }, { name: 'VVIP', hex: '01215A' }
      ]
    };
  },
  computed: {
    todayStr() { const d = new Date(); const m = String(d.getMonth()+1).padStart(2,'0'); const dd = String(d.getDate()).padStart(2,'0'); return d.getFullYear()+'-'+m+'-'+dd; },
    // 3008: 현재 월(YYYY-MM). 월 선택기 기본값/최댓값.
    curMonth() { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0'); },
    // 3008: 선택한 월(selectedMonth)에 해당하는 스냅샷 항목만 + 마지막 '현재'.
    snapOptions() {
      const opts = [];
      const mon = this.selectedMonth || '';
      for (const p of this.snapPoints) {
        if (mon && String(p.dt).slice(0, 7) !== mon) continue;   // 해당 월 항목만
        opts.push({ value: p.t + '|' + p.dt, label: this.fmtDt(p.dt) + ' 수당계산' + (p.t === 'AFTER' ? '직후' : '직전') });
      }
      opts.push({ value: 'CURRENT', label: '현재' });
      return opts;
    },
    // 3008: 선택 시점 파싱. 'CURRENT' → {current:true}, 'BEFORE|dt'/'AFTER|dt' → {current:false, snapType, baseDate}
    selectedParsed() {
      if (!this.selectedPoint || this.selectedPoint === 'CURRENT') return { current: true };
      const i = this.selectedPoint.indexOf('|');
      if (i < 0) return { current: true };
      return { current: false, snapType: this.selectedPoint.slice(0, i), baseDate: this.selectedPoint.slice(i + 1) };
    },
    floorH() { return this.nodeH + this.vGap; },
    frameX() { return this.gutter; },
    frameY() { return this.pad; },
    frameW() { return this.svgW - this.gutter - this.pad; },
    frameH() { return this.svgH - this.frameY - this.pad; },
    roofPoints() {
      const x1 = this.frameX, x2 = this.frameX + this.frameW;
      const yBase = this.frameY, apex = this.frameX + this.frameW / 2;
      const yTop = Math.max(3, this.frameY - 15);
      return x1 + ',' + yBase + ' ' + apex + ',' + yTop + ' ' + x2 + ',' + yBase;
    },
    shownFloorsLabel() {
      return this.shownFloors.length ? this.shownFloors.join(', ') + '단' : '-';
    }
  },
  watch: {
    // 3008: 월 변경 시, 선택 시점을 '현재'로 되돌린다(다른 달 항목이 선택에 남지 않도록).
    selectedMonth() { this.selectedPoint = 'CURRENT'; }
  },
  created() {
    // 20007: 초기 진입 - role 무관 센터명/이름 에디트 비우기(비활성), 검색·비우기 버튼 활성. 비율 100%, 단수 1 고정, 좌/우측 체크. 자동 조회 없음.
    //조직도 팝업창 내 이름 조회 입력받기 프로젝트
    this.custName = ''; this.custLoginId = '';
    this.centerId = null; this.centerName = '';
    this.searchOpen = false;
    this.centerOpen = false;
    // 개선2_61: 멤버쉽 지도는 단수 1 고정, 노드 높이 확대(금액줄), 초기 0~14단
    this.level = 1;   // 개선2_63: 후원/멤버쉽 공통 단수 1 고정(비활성)
    if (this.membership) this.nodeH = 92;   // 스펙24003: 가입일/첫구매일/최근재구매일 + 멤버쉽 금액 줄 높이 확대
    // 3008: 시점 콤보는 admin 전용. admin 이면 목록 로드(기본=최신 수당계산직후), 그 외는 항상 '현재'(라이브 GRE_NODE).
    this.isAdminUser = isAdmin();
    if (this.isAdminUser) this.loadSnapPoints();
    this.shownDepthCap = 15;
    this.side = 'B';
    this.zoom = 1;   // 스펙104/개선2_63: 진입 그래픽 비율 100%
    // 스펙103: 직급 색상은 GRE_COM_CODE(POS_COLOR)에서 관리 → 서버에서 로드(실패 시 하드코딩 폴백).
    this.loadRankColors();
  },
  methods: {
    close() { this.$emit('close'); },
    // 20007: 이름 항목 회원 검색 팝업 선택 완료 → 이름 에디트에 표시, 조회 버튼으로 도식 표시.
    onUserSelect(sel) {
      this.searchOpen = false;
      if (!sel || !sel.loginId) return;
      //조직도 팝업창 내 이름 조회 입력받기 프로젝트
      this.custName = sel.name || ''; this.custLoginId = sel.loginId;
      this.error = '';
    },
    // 20007: 센터 항목 센터 검색 팝업 선택 완료 → 센터명 에디트에 표시.
    onCenterSelect(sel) {
      this.centerOpen = false;
      if (!sel) return;
      this.centerId = sel.id != null ? sel.id : null;
      this.centerName = sel.centerName || sel.name || '';
      this.error = '';
    },
    // 20007: 비우기 버튼
    clearCenter() { this.centerId = null; this.centerName = ''; },
    //조직도 팝업창 내 이름 조회 입력받기 프로젝트
    clearMember() { this.custName = ''; this.custLoginId = ''; },
    // 스펙103: 직급 색상표를 GRE_COM_CODE POS_COLOR 에서 로드. val = "hex"(단색) 또는 "hex1,hex2"(그라데이션).
    async loadRankColors() {
      try {
        const res = await api.refList('POS_COLOR');
        const rows = (res && res.data) || [];
        if (rows.length) {
          this.rankColorList = rows.map(r => {
            const val = (r.val || '').trim();
            return val.indexOf(',') >= 0 ? { name: r.name, grad: true } : { name: r.name, hex: val };
          });
        }
      } catch (e) { /* 폴백: 하드코딩 rankColorList 유지 */ }
    },
    // 스펙104 색상 규칙: 통산매출0 또는 Non-active → 흰색. Active & 통산매출>0 → 직급별 색상.
    rankInfo(name) {
      return this.rankColorList.find(r => r.name === (name || '').trim()) || null;
    },
    // 스펙208: 색상표/네모 글자만 그린스타B → 그린스타 로 표기(색상 009C00 은 유지)
    displayRank(name) {
      return (name || '').trim() === '그린스타B' ? '그린스타' : name;
    },
    nodeFill(n) {
      // 스펙104: 통산 매출 0 이면 (액티브 여부 무관) 흰색. Non-active 도 흰색.
      if (!n.active || Number(n.sales || 0) === 0) return '#ffffff';
      const r = this.rankInfo(n.rankName);
      if (!r) return '#ffffff';
      if (r.grad) return 'url(#ptGradDia)';                   // 블루/레드 다이아몬드
      return '#' + r.hex;
    },
    nodeStroke(n) {
      if (!n.active || Number(n.sales || 0) === 0) return '#b6c1d6';
      return '#33405c';
    },
    nodeText(n) {
      // 밝은 채움엔 검정 글자, 어두운 채움엔 흰 글자
      const fill = this.nodeFill(n);
      if (fill === '#ffffff') return '#1f2a44';
      if (fill.indexOf('url(') === 0) return '#ffffff';
      const hex = fill.slice(1);
      const r = parseInt(hex.slice(0, 2), 16), g = parseInt(hex.slice(2, 4), 16), b = parseInt(hex.slice(4, 6), 16);
      const lum = (0.299 * r + 0.587 * g + 0.114 * b);
      return lum > 150 ? '#1f2a44' : '#ffffff';
    },
    zoomIn() { this.zoom = Math.min(1, Math.round((this.zoom + 0.1) * 10) / 10); },
    zoomOut() { this.zoom = Math.max(0.1, Math.round((this.zoom - 0.1) * 10) / 10); },
    // 스펙207 표시할 단(floor) 집합. (개정: ±2 → ±3)
    //  단수 1 : 해당 단(1) / 아래 3개 단(2,3,4)
    //  그 외  : 1단 / 상위 3개 단(N-3,N-2,N-1) / 해당 단(N) / 하위 3개 단(N+1,N+2,N+3)
    computeShownFloors(n) {
      const set = new Set([1]);
      if (n === 1) { set.add(1); set.add(2); set.add(3); set.add(4); }
      else {
        for (let f = n - 3; f <= n + 3; f++) { if (f >= 1) set.add(f); }
      }
      return Array.from(set).sort((a, b) => a - b);
    },
    doSearch() {
      const n = Number(this.level);
      if (!Number.isInteger(n) || n < 1) {
        this.error = '단수는 1 이상으로 입력하세요.'; this.layoutNodes = []; return;
      }
      // 스펙104: 선택된 사용자(로그인ID) 기준으로 조직도 표시.

      //조직도 팝업창 내 이름 조회 입력받기 프로젝트
      const id = String(this.custLoginId || '').trim();
      const name = String(this.custName || '').trim();
      if (!id && !name) { this.error = '이름을 입력하거나 검색 버튼으로 회원을 선택하세요.'; this.layoutNodes = []; return; }
      this.fetch({ targetLoginId: id, custName: name });

    },
    async fetch(params) {
      this.loading = true;
      this.error = '';
      try {
        // 3008: 선택 시점에 따라 조회.
        //   · '현재' → 라이브(GRE_NODE) : 멤버쉽=placementTreeMembership, 후원=placementTree
        //   · '수당계산직전/직후' → 이력 스냅샷(GRE_NODE_HIST type=BEFORE/AFTER, dt=배치일)
        const sel = this.selectedParsed;
        // 3008: 금액 표시 여부 확정 - 현재/수당계산직후만 표시(수당계산직전은 숨김).
        this.shownAmounts = sel.current || sel.snapType === 'AFTER';
        let res;
        if (this.membership) {
          res = sel.current
            ? await api.placementTreeMembership(params)
            : await api.placementTreeMembershipHist(Object.assign({}, params, { baseDate: sel.baseDate, snapType: sel.snapType }));
        } else {
          res = sel.current
            ? await api.placementTree(params)
            : await api.placementTreeHist(Object.assign({}, params, { baseDate: sel.baseDate, snapType: sel.snapType }));
        }
        const d = res.data;
        if (!d || !d.root) { this.error = '계보 정보를 찾을 수 없습니다.'; this.layoutNodes = []; return; }
        this.maxDepthLimit = d.maxDepthLimit || 25;
        this.rootLabel = d.root.name + ' (' + d.root.loginId + ')';
        this.shownDepthCap = 15;   // 개선2_61: 조회 시 0~14단부터
        this.treeRoot = d.root;    // 지연로딩 재배치용
        this.buildLayout(d.root);
        this.zoom = 1;   // 스펙104/개선2_63: 조회 완료시 그래픽 비율 100%
        this.$nextTick(() => this.ensureScrollable());
      } catch (e) {
        this.error = e.message || '조회 중 오류가 발생했습니다.';
        this.layoutNodes = [];
      } finally {
        this.loading = false;
      }
    },
    // 좌/우 한쪽 서브트리만 + 단수 밴드 필터로 배치.
    buildLayout(root) {
      // 개선2_63: 후원/멤버쉽 공통으로 아래 shownDepthCap 단까지 지연로딩(±3 밴드 방식 폐지)
      // 1) 좌/우 선택: 기준(1단) + 선택한 쪽 자식 서브트리만.
      //    원본 트리를 순회하되, 기준의 반대편 최상위 자식만 잘라낸다.
      const all = [];              // 방문 노드
      const edgePairs = [];        // {parentId, childId, pFloor, cFloor}
      const visit = (node, parentId) => {
        if (!node) return;
        all.push(node);
        if (parentId != null) {
          edgePairs.push({ parentId: parentId, childId: node.id, pFloor: node.floor - 1, cFloor: node.floor });
        }
        visit(node.left, node.id);
        visit(node.right, node.id);
      };
      const trimmed = Object.assign({}, root);
      // 스펙104: 좌(L)=우측 자르기, 우(R)=좌측 자르기, 좌/우측(B)=양쪽 모두 표시(자르지 않음)
      if (this.side === 'L') trimmed.right = null;
      else if (this.side === 'R') trimmed.left = null;
      visit(trimmed, null);

      // 2) 단수 필터 (개선2_63: 후원 조직도/멤버쉽 지도 공통 - 아래로 shownDepthCap 단까지 표시, 10단씩 지연로딩)
      const visible = all.filter(nd => nd.floor <= this.shownDepthCap);
      const visibleIds = new Set(visible.map(nd => nd.id));
      this.shownFloors = Array.from(new Set(visible.map(nd => nd.floor))).sort((a, b) => a - b);

      if (!visible.length) { this.layoutNodes = []; this.layoutEdges = []; this.floorBands = []; return; }

      // 3) 타이디 트리 배치 (표시 대상만). 표시되는 floor 를 위→아래 순서(rowIndex)로 압축.
      const floorsSorted = Array.from(new Set(visible.map(n => n.floor))).sort((a, b) => a - b);
      const rowOf = {};
      floorsSorted.forEach((f, i) => { rowOf[f] = i; });

      const stepX = this.nodeW + this.hGap;
      const originX = this.gutter + this.pad;
      const originY = this.pad + 8;   // 스펙24003: 조회 버튼 아래 흰색 상하 여백 축소
      let leafCounter = 0;

      // slot 계산: 표시되는 노드만으로 리프/중앙 배치
      const childrenVisible = (nd) => {
        const cs = [];
        if (nd.left && visibleIds.has(nd.left.id)) cs.push(nd.left);
        if (nd.right && visibleIds.has(nd.right.id)) cs.push(nd.right);
        return cs;
      };
      const slotMap = {};
      const assign = (nd) => {
        if (!nd || !visibleIds.has(nd.id)) return null;
        const cs = childrenVisible(nd);
        let slot;
        if (!cs.length) { slot = leafCounter++; }
        else {
          const ss = cs.map(c => assign(c)).filter(s => s !== null);
          slot = ss.reduce((a, b) => a + b, 0) / ss.length;
        }
        slotMap[nd.id] = slot;
        return slot;
      };
      // 표시되는 최상위(=1단 루트가 보이면 루트, 아니면 각 밴드 최상위)들을 배치
      const roots = visible.filter(nd => {
        // 부모가 표시되지 않으면 이 노드가 화면상 최상위
        const parent = edgePairs.find(e => e.childId === nd.id);
        return !parent || !visibleIds.has(parent.parentId);
      });
      roots.forEach(r => assign(r));

      const nodes = [];
      const posOf = {};
      visible.forEach(nd => {
        const slot = slotMap[nd.id] != null ? slotMap[nd.id] : (leafCounter++);
        const x = originX + slot * stepX;
        const y = originY + rowOf[nd.floor] * this.floorH;
        posOf[nd.id] = { x: x, y: y };
        nodes.push({
          id: nd.id, x: x, y: y,
          name: nd.name, loginId: nd.loginId, rankName: nd.rankName,
          joinDt: nd.joinDt || '',   // 23001: 네모칸 직급 아래 가입일
          firstBuyDt: nd.firstBuyDt || '',            // 스펙24003: 첫구매일
          lastRepurchaseDt: nd.lastRepurchaseDt || '',// 스펙24003: 최근 재구매일(없으면 미표시)
          active: !!nd.active, paid: Number(nd.paid || 0), sales: Number(nd.sales || 0), floor: nd.floor, leg: nd.leg || '',
          membershipCb: Number(nd.membershipCb || 0),   // 개선2_61: 어제(최신일) 멤버쉽
          membershipTot: Number(nd.membershipTot || 0)   // 개선2_62: 통산 합계 멤버쉽
        });
      });

      // 4) 간선: 부모/자식 모두 표시되고 행이 인접(rowIndex 차이 1)일 때만
      const edges = [];
      edgePairs.forEach(e => {
        if (!visibleIds.has(e.parentId) || !visibleIds.has(e.childId)) return;
        if (rowOf[e.cFloor] - rowOf[e.pFloor] !== 1) return;
        const p = posOf[e.parentId], c = posOf[e.childId];
        if (!p || !c) return;
        const px = p.x + this.nodeW / 2, py = p.y + this.nodeH;
        const cx = c.x + this.nodeW / 2, cy = c.y;
        const midY = (py + cy) / 2;
        edges.push({ d: 'M' + px + ',' + py + ' V' + midY + ' H' + cx + ' V' + cy });
      });

      // 5) 캔버스/층 띠
      const maxX = nodes.reduce((m, nd) => Math.max(m, nd.x + this.nodeW), originX + this.nodeW);
      this.svgW = maxX + this.pad;
      this.svgH = originY + floorsSorted.length * this.floorH + this.pad;
      this.floorBands = floorsSorted.map((f, i) => ({ floor: f, y: originY + i * this.floorH - 6 }));
      this.layoutNodes = nodes;
      this.layoutEdges = edges;
    },
    // 개선2_61: 멤버쉽 금액 천단위 콤마
    memFmt(v) { return Number(v || 0).toLocaleString(); },
    // 3010(MEMO): admin 전용 - 네모 클릭 시 노드 정보(사업자/소비자, 좌/우 추천자 수, 멤버쉽 스캔범위) 메시지박스.
    async onNodeClick(n) {
      if (!this.isAdminUser || !n || n.id == null) return;
      try {
        const res = await api.nodeInfo(n.id);
        const d = (res && res.data) || {};
        const msg =
          '이름: ' + (n.name || '') + ' (' + (n.loginId || '') + ')\n' +
          '직급: ' + (d.rankName || '-') + '\n' +
          '구분: ' + (d.bizLabel || (d.isBusiness ? '사업자' : '소비자')) + '\n' +
          '좌 추천자 수: ' + (d.leftRefCnt || 0) + ' (Active ' + (d.leftActiveCnt || 0) + ')\n' +
          '우 추천자 수: ' + (d.rightRefCnt || 0) + ' (Active ' + (d.rightActiveCnt || 0) + ')\n' +
          '멤버쉽 스캔 범위: ' + (d.memScanRange || 0) + '단';
        window.alert(msg);
      } catch (e) {
        window.alert('노드 정보 조회 실패: ' + ((e && e.message) || '오류'));
      }
    },
    // 3008: 배치일 문자열 'YYYY-MM-DD' → 'YYYY년-MM월-DD일'
    fmtDt(dt) {
      const p = String(dt || '').split('-');
      return p.length < 3 ? String(dt || '') : (p[0] + '년-' + p[1] + '월-' + p[2] + '일');
    },
    // 3008: 시점 콤보 목록 로드. 전체 스냅샷을 보관하고, 콤보 표시는 선택 월(selectedMonth)로 필터. 진입 기본=현재.
    async loadSnapPoints() {
      try {
        const res = await api.histSnapshotPoints();
        const pts = (res && res.data) ? res.data : [];
        const list = [];
        for (const p of pts) {
          const dt = String(p.dt || '').slice(0, 10);
          const t = String(p.snapType || p.type || '').toUpperCase();
          if (!dt || (t !== 'BEFORE' && t !== 'AFTER')) continue;
          list.push({ dt: dt, t: t });
        }
        this.snapPoints = list;
      } catch (e) {
        this.snapPoints = [];
      }
      this.selectedMonth = this.curMonth;   // 월 선택기 기본 = 현재 월
      this.selectedPoint = 'CURRENT';        // 진입 기본 = 현재(라이브 GRE_NODE)
    },
    // 개선2_76/3008: 멤버쉽 지도 - 2번째 행(floor>=2)부터, 기준일 멤버쉽 금액이 0 초과인 네모칸 감지 → 적색 테두리
    memHasAmt(n) {
      if (!this.membership) return false;
      if (!(n.floor >= 2)) return false;
      return Number(n.membershipCb || 0) > 0;
    },
    // 개선2_61: 아래로 끝까지 스크롤하면 10단 추가 로드
    onCanvasScroll(e) {
      if (!this.treeRoot) return;
      const el = e.target;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
        if (this.shownDepthCap < this.maxFloorOf(this.treeRoot)) {
          this.shownDepthCap += 10;
          this.buildLayout(this.treeRoot);
          this.$nextTick(() => this.ensureScrollable());
        }
      }
    },
    // 개선2_61: 초기 표시분이 뷰포트를 못 채워 스크롤이 안 생기면, 스크롤 생길 때까지 10단씩 미리 확장
    ensureScrollable() {
      if (!this.treeRoot) return;
      const el = this.$el ? this.$el.querySelector('.pt-body') : null;
      if (!el) return;
      if (el.scrollHeight <= el.clientHeight + 4 && this.shownDepthCap < this.maxFloorOf(this.treeRoot)) {
        this.shownDepthCap += 10;
        this.buildLayout(this.treeRoot);
        this.$nextTick(() => this.ensureScrollable());
      }
    },
    maxFloorOf(root) {
      let mx = 1;
      const walk = (nd) => { if (!nd) return; if (nd.floor > mx) mx = nd.floor; walk(nd.left); walk(nd.right); };
      walk(root);
      return mx;
    },
    // 개선2_62: 멤버쉽 지도 - 마우스 좌클릭 드래그로 상하(및 좌우) 자유 이동(팬), 커서 손 아이콘
    onPanStart(e) {
      if (e.button !== 0) return;
      const el = e.currentTarget;
      this.panning = true;
      this.panX = e.clientX; this.panY = e.clientY;
      this.panSL = el.scrollLeft; this.panST = el.scrollTop;
      el.classList.add('pt-grabbing');
      e.preventDefault();   // 드래그 중 텍스트/이미지 선택 방지
    },
    onPanMove(e) {
      if (!this.panning) return;
      const el = e.currentTarget;
      el.scrollLeft = this.panSL - (e.clientX - this.panX);
      el.scrollTop  = this.panST - (e.clientY - this.panY);
    },
    onPanEnd(e) {
      if (!this.panning) return;
      this.panning = false;
      if (e.currentTarget && e.currentTarget.classList) e.currentTarget.classList.remove('pt-grabbing');
    }
  }
};
</script>

<style scoped>
.pt-mask {
  position: fixed; inset: 0; background: rgba(15, 22, 40, 0.55);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
}
.pt-modal {
  /* 20007: 팝업 가로 길이 화면 최대치 */
  background: #fff; border-radius: 12px; width: 99vw; max-width: none; max-height: 92vh;
  display: flex; flex-direction: column; box-shadow: 0 18px 60px rgba(0,0,0,0.35);
}
/* 개선2_61: 멤버쉽 지도는 팝업 가로 길이 화면 끝까지 */
.pt-modal-full { width: 100vw; max-width: 100vw; border-radius: 0; }
.pt-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 13px 20px; border-bottom: 1px solid #e6ebf3;
}
.pt-head h4 { margin: 0; font-size: 16px; color: #1f2a44; }
.pt-head-right { display: flex; align-items: center; gap: 14px; }
.pt-legend { font-size: 12px; color: #55617a; display: inline-flex; align-items: center; gap: 5px; }
.pt-legend .dot { width: 11px; height: 11px; border-radius: 3px; display: inline-block; border: 1px solid #cdd6e6; }
.dot.active { background: #2f6fed; border-color: #2f6fed; }
.dot.inactive { background: #ffffff; }
.pt-x {
  border: 1px solid #cfd6e6; background: #f4f6fa; border-radius: 6px;
  padding: 5px 12px; cursor: pointer; font-size: 13px; color: #33405c;
}
.pt-x:hover { background: #e9eef7; }

.pt-toolbar {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 10px 20px; background: #f7f9fc; border-bottom: 1px solid #eef2f8;
}
.pt-toolbar label { font-size: 13px; color: #33405c; font-weight: 600; }
.pt-toolbar .pt-user { font-size: 13px; color: #1f2a44; font-weight: 600; min-width: 140px;
  padding: 4px 8px; background: #eef3fb; border: 1px solid #d7e0ef; border-radius: 6px; }
.pt-toolbar input[type="text"] {
  height: 30px; border: 1px solid #cfd6e6; border-radius: 6px; padding: 0 10px;
  font-size: 13px; width: 170px;
}
.pt-toolbar input.lvl {
  height: 30px; border: 1px solid #cfd6e6; border-radius: 6px; padding: 0 8px;
  font-size: 13px; width: 62px;
}
.pt-toolbar select.nodesel {
  height: 30px; border: 1px solid #cfd6e6; border-radius: 6px; padding: 0 8px;
  font-size: 13px; min-width: 120px; background: #fff;
}
.pt-toolbar button.primary {
  height: 30px; border: none; background: #4d8bff; color: #fff; border-radius: 6px;
  padding: 0 14px; cursor: pointer; font-size: 13px;
}
.pt-toolbar button.primary:hover { background: #3f79e8; }
/* 20007: 센터/이름 검색조건 에디트(비활성) + 비우기 버튼 */
.pt-toolbar input.cond {
  /* 20007: 센터/이름 에디트 가로 길이 축소 */
  height: 30px; border: 1px solid #cfd6e6; border-radius: 6px; padding: 0 10px;
  font-size: 13px; width: 110px; background: #f2f4f7; color: #667085;
}

/* 조직도 팝업창 내 이름 조회 입력받기 프로젝트 */
.pt-toolbar input.cond-edit {
  background: #fff;
  color: #1f2a44;
}

.pt-toolbar button.ghost {
  height: 30px; border: 1px solid #c0c6d0; background: #f2f4f7; color: #444;
  border-radius: 6px; padding: 0 12px; cursor: pointer; font-size: 13px;
}
.pt-toolbar button.ghost:hover { background: #e9edf3; }
.pt-radios { display: inline-flex; gap: 12px; align-items: center; }
.pt-radios .rd { font-size: 13px; color: #33405c; font-weight: 500; display: inline-flex; align-items: center; gap: 4px; }
.pt-sep { width: 1px; height: 22px; background: #dbe2ee; margin: 0 4px; }
.pt-zoom-label { font-size: 12px; color: #55617a; }
.pt-zoom {
  width: 30px; height: 30px; border: 1px solid #cfd6e6; background: #fff; border-radius: 6px;
  cursor: pointer; font-size: 16px; line-height: 1; color: #33405c;
}
.pt-zoom:hover:not(:disabled) { background: #eef3fb; }
.pt-zoom:disabled { opacity: 0.4; cursor: not-allowed; }
.pt-zoom-val { font-size: 12px; color: #33405c; min-width: 42px; text-align: center; font-weight: 600; }
.pt-basedate { height: 26px; border: 1px solid #cfd6e6; border-radius: 6px; padding: 0 6px; font-size: 12px; color: #33405c; }
.pt-snapsel { height: 26px; border: 1px solid #cfd6e6; border-radius: 6px; padding: 0 6px; font-size: 12px; color: #33405c; background: #fff; max-width: 240px; }
.pt-summary-inline { font-size: 12.5px; color: #55617a; margin-left: auto; }

.pt-body { overflow: auto; padding: 12px; flex: 1; }
/* 개선2_62: 멤버쉽 지도 드래그 팬 - 커서 손 아이콘 */
.pt-grab { cursor: grab; }
.pt-grabbing { cursor: grabbing !important; user-select: none; }
.pt-msg { padding: 40px; text-align: center; color: #6b7691; font-size: 14px; }
.pt-msg.err { color: #c0392b; }
/* 개선2_61: 스크롤 컨테이너는 pt-body 단일(스크롤 이벤트 신뢰성) */
.pt-canvas { display: inline-block; }

.pt-roof { fill: #37507d; stroke: #2a3c5e; stroke-width: 1; }
.pt-frame { fill: #fbfcfe; stroke: #37507d; stroke-width: 2; }
.pt-band.odd { fill: #f2f5fb; }
.pt-band.even { fill: #e9eef8; }
.pt-floor-line { stroke: #d3dcec; stroke-width: 1; }
.pt-floor-label { fill: #6b7691; font-size: 11px; font-weight: 700; }
.pt-edge { fill: none; stroke: #9fb0cc; stroke-width: 1.4; }

/* 스펙24: Active=청색, Non-active=흰색 */
.pt-node { stroke-width: 1.4; }
.pt-node-name { font-size: 11px; font-weight: 700; }
.pt-node-sub { font-size: 9.5px; }
.pt-node-rank { font-size: 9.5px; font-weight: 600; }
.pt-node-join { font-size: 9.5px; opacity: 0.9; }   /* 스펙24003: 가입일/첫구매일/최근재구매일 9.5px */
.pt-node-mem { font-size: 10px; font-weight: 700; }
.pt-leg-badge { font-size: 9px; font-weight: 700; }

/* 스펙27 직급 색상표 범례 */
.pt-colorbar {
  display: flex; align-items: center; gap: 10px 14px; flex-wrap: wrap;
  padding: 8px 20px; background: #fff; border-bottom: 1px solid #eef2f8;
}
.pt-colorbar-title { font-size: 12px; font-weight: 700; color: #33405c; }
.pt-color-item { font-size: 11px; color: #55617a; display: inline-flex; align-items: center; gap: 4px; }
.pt-color-item .sw { width: 13px; height: 13px; border-radius: 3px; display: inline-block; border: 1px solid #cdd6e6; }
</style>
